import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, CircularProgress } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import DialogForm from "@/components/modal/DialogForm";
import FormikSelectInput from "@/components/forms/FormikSelectInput";
import { StyledButton } from "@/components";
import { toast } from "@/components/toastify";
import {
  useGetDayTypeListQuery,
  useUpsertDayTypeMutation,
} from "@/api/services/attendanceManagement/shiftRoster/ShiftRoster";

const validationSchema = Yup.object({
  dayTypeId: Yup.string().required("Please select a day type"),
});

const EditDayTypeModal = ({ open, setOpen, empId, date }) => {
  const [upsertDayType] = useUpsertDayTypeMutation();
  const { data: dayTypeListData, isFetching: isDayTypeLoading } =
    useGetDayTypeListQuery();

  const dayTypeList = useMemo(() => {
    const list = Array.isArray(dayTypeListData)
      ? dayTypeListData
      : dayTypeListData?.data || [];
    return list.map((item) => ({
      value: item.dayTypeId,
      label: item.dayTypeName || item.name || item.description || item.dayType,
      dayType: item.dayType,
    }));
  }, [dayTypeListData]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const selected = dayTypeList.find((o) => o.value === values.dayTypeId);
      if (!empId || !date) {
        toast("error", "Missing employee or date");
        setSubmitting(false);
        return;
      }
      await upsertDayType({
        empId,
        date,
        dayTypeId: selected?.value,
        dayType: selected?.dayType,
      }).unwrap();
      toast("success", "Day type updated successfully");
      resetForm();
      setOpen(false);
    } catch (error) {
      toast(
        "error",
        error?.data?.message || "Failed to update day type"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogForm
      open={open}
      setOpen={setOpen}
      title={`Edit Day Type${date ? ` - ${date}` : ""}`}
      width="xs"
    >
      <Formik
        initialValues={{ dayTypeId: "" }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormikSelectInput
                  name="dayTypeId"
                  label="Day Type"
                  placeholder={
                    isDayTypeLoading ? "Loading..." : "Select Day Type"
                  }
                  options={dayTypeList}
                  disabled={isDayTypeLoading}
                  search={true}
                  required
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={1} mt={1}>
              <Grid item>
                <StyledButton
                  label="Cancel"
                  variant="outlined"
                  type="button"
                  onClick={() => setOpen(false)}
                />
              </Grid>
              <Grid item>
                <StyledButton
                  label={
                    isSubmitting ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Submit"
                    )
                  }
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </DialogForm>
  );
};

EditDayTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  empId: PropTypes.string,
  date: PropTypes.string,
};

export default EditDayTypeModal;
